import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useToast } from '../hooks/useToast';
import { useAuth } from '../features/auth/context';
import { useDocumentTitle } from '../shared/useDocumentTitle';
import { analytics } from '../shared/analytics';
import NameDialog from '../components/NameDialog';
import TripDetailNav from '../features/my-trips/components/TripDetailNav';
import MainTag from '../features/my-trips/components/MainTag';
import InfoPills from '../features/my-trips/components/InfoPills';
import JourneyTips from '../features/my-trips/components/JourneyTips';
import RouteTimeline, { fmtDayRange } from '../features/my-trips/components/RouteTimeline';
import LegSheet, { LegBookingLinks } from '../features/my-trips/components/LegSheet';
import StopSheet from '../features/my-trips/components/StopSheet';
import { changeStopIds, computeJourneySchedule, msToIsoDay } from '../features/my-trips/utils';
import { isNightTrainMode } from '../shared/transport';
import ApproxNote from '../features/my-trips/components/ApproxNote';
import JourneyRouteMap from '../features/maps/JourneyRouteMap';
import DetailSplitLayout from '../features/my-trips/components/DetailSplitLayout';
import { useJourneyRouteMap } from '../features/my-trips/useJourneyRouteMap';
import {
  SharedJourneyPayload,
  SharedAccessProblem,
  fetchSharedJourney,
  classifyShareError,
  storeSharePassword,
  saveSharedCopy,
  fetchSharedLegOmioLink,
  fetchSharedNightTrainBookingLink,
  fetchSharedStopBookingLink,
} from '../features/share/api';
import {
  useNoIndex,
  useOwnerRedirect,
  SharedNavActions,
  SharedTripStrip,
  SharePasswordGate,
  ShareNotFound,
  ShareExpired,
  ShareTooManyAttempts,
  ShareLoadError,
  ShareWallDialog,
} from '../features/share/visitor';
import SharedShareDialog from '../features/share/SharedShareDialog';

/**
 * Trip sharing v1 — public, read-only journey detail behind
 * /s/<token>/journeys/<journeyId>. Reuses the journey building blocks (the
 * desktop split with the map on the right and useJourneyRouteMap, info
 * pills, route timeline) with every edit affordance kept VISIBLE but
 * routed to the ShareWallDialog (product decision 2026-09-10). The stop/leg
 * sheets open READ-ONLY (rows/pills pass the capture gate); the visitor's
 * two funnel actions (Share, Save a copy) are visible nav buttons (UX
 * decision 2026-09-14). The booking buttons (tickets, night trains, stays)
 * work for visitors as they do for the owner (Paulo 2026-09-28, product
 * backlog #51), through the share-token endpoints in features/share/api.ts.
 */
const SharedJourneyPage: React.FC = () => {
  const { token, journeyId } = useParams<{ token: string; journeyId: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { user, loading: authLoading } = useAuth();
  const signedIn = !!user;

  useNoIndex();
  // Owner on their own link → their journey page; the shared view waits for it.
  const ownerChecked = useOwnerRedirect(token, journeyId);

  const [payload, setPayload] = useState<SharedJourneyPayload | null>(null);
  const [problem, setProblem] = useState<SharedAccessProblem | null>(null);
  const [loading, setLoading] = useState(true);
  const [wallOpen, setWallOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [savingCopy, setSavingCopy] = useState(false);
  const viewTrackedRef = useRef(false);

  const journey = payload?.journey ?? null;
  useDocumentTitle(journey?.title || 'Shared trip');

  const load = useCallback(async () => {
    if (!token || !journeyId) return;
    setLoading(true);
    try {
      const data = await fetchSharedJourney(token, journeyId);
      setPayload(data);
      setProblem(null);
    } catch (e) {
      setProblem(classifyShareError(e));
    } finally {
      setLoading(false);
    }
  }, [token, journeyId]);

  useEffect(() => { if (ownerChecked) load(); }, [ownerChecked, load]);

  // Fires once the payload AND auth have both resolved (whichever finishes
  // last), so `signed_in` is never read while useAuth is still loading — a
  // signed-in visitor's fast payload fetch used to beat auth resolution and
  // get permanently mis-logged as anonymous (ultrareview finding,
  // 2026-09-14; viewTrackedRef stays a one-shot latch, now here).
  useEffect(() => {
    if (!payload || authLoading || viewTrackedRef.current) return;
    viewTrackedRef.current = true;
    analytics.trackTripShareLinkViewed({
      trip_id: payload.journey.tripId || '',
      visibility: payload.share.visibility,
      signed_in: signedIn,
      surface: 'journey',
    });
  }, [payload, authLoading, signedIn]);

  const openWall = useCallback(() => setWallOpen(true), []);

  // ── Read-only detail sheets (Paulo 2026-09-11) ─────────────────────────────
  // Visitors can OPEN the stop and leg sheets (they carry the trip's substance:
  // schedule, prices, booked state, and — with the owner's opt-in — stay and
  // ticket details). journeyId={null} puts both sheets in display mode: every
  // internal fetch (compare facts/prices, previews) is guarded on journeyId
  // and silently skipped; the booking buttons get their links from the
  // share-token fetchers passed in below. Edit controls inside stay visible;
  // the capture wrapper below walls them.
  const [openLegId, setOpenLegId] = useState<string | null>(null);
  const [openStopId, setOpenStopId] = useState<string | null>(null);
  const openLeg = useMemo(() => (journey?.legs || []).find((l: any) => l.id === openLegId) || null, [journey, openLegId]);
  const openStop = useMemo(() => (journey?.stops || []).find((s: any) => s.id === openStopId) || null, [journey, openStopId]);
  // The route map, as on the owner's Journey detail: on the right on
  // desktop, following the timeline and the open sheet, stops pickable on
  // it (a second click opens the read-only stop sheet). No sticky header
  // band here, so below the split the map stays inline.
  const routeMap = useJourneyRouteMap({ journey, openLegId, openStopId, onOpenStop: setOpenStopId });
  const openLegStops = useMemo(() => {
    if (!openLeg) return { from: '', to: '' };
    const byId = (sid: string) => (journey?.stops || []).find((s: any) => s.id === sid);
    return { from: byId(openLeg.fromStopId)?.city || '', to: byId(openLeg.toStopId)?.city || '' };
  }, [journey, openLeg]);
  // Changes: stops with 0 nights between two others. Only the night-train
  // sheet says so ("Change in …"); the stops themselves look like any other.
  const changeIds = useMemo(() => changeStopIds(journey?.stops || []), [journey]);
  const openLegExpectedDate = useMemo(() => {
    if (!openLeg || !journey?.departureDate) return null;
    const day = computeJourneySchedule(journey.stops || [], journey.legs || [], journey.departureDate).legDays[openLeg.id];
    return day != null ? msToIsoDay(day.ms) : null;
  }, [journey, openLeg]);
  const openStopWindow = useMemo(() => {
    const empty = { checkin: null as string | null, checkout: null as string | null, label: null as string | null };
    if (!openStop || !journey?.departureDate) return empty;
    const w = computeJourneySchedule(journey.stops || [], journey.legs || [], journey.departureDate).stopWindows[openStop.id];
    if (!w) return empty;
    return {
      checkin: msToIsoDay(w.fromMs),
      checkout: msToIsoDay(w.toMs),
      label: w.toMs >= w.fromMs ? fmtDayRange(w.fromMs, w.toMs) : null,
    };
  }, [journey, openStop]);

  // ── Booking buttons (tickets, night trains, stays) ─────────────────────────
  // Same click pattern as the owner's page: the tab opens synchronously so
  // popup blockers allow it, then the link arrives. The click is tracked
  // here, in the browser (anonymous visitor); a night train sold on the
  // operator's own site is not an affiliate click and is not tracked.
  const [busyLegId, setBusyLegId] = useState<string | null>(null);
  const [busyStopId, setBusyStopId] = useState<string | null>(null);
  const openInNewTab = useCallback((request: Promise<{ url: string }>, failTitle: string): Promise<void> => {
    const win = window.open('', '_blank');
    return request
      .then(({ url }) => {
        if (win) win.location.href = url;
        else window.open(url, '_blank');
      })
      .catch(() => {
        if (win) win.close();
        showToast({ title: failTitle, message: 'Please try again.', type: 'error', duration: 4000 });
      });
  }, [showToast]);
  const legContext = useCallback((legId: string) => {
    const leg = (journey?.legs || []).find((l: any) => l.id === legId);
    const stopOf = (stopId?: string) => (journey?.stops || []).find((s: any) => s.id === stopId);
    const from = stopOf(leg?.fromStopId);
    const to = stopOf(leg?.toStopId);
    const day = journey ? computeJourneySchedule(journey.stops || [], journey.legs || [], journey.departureDate).legDays[legId] : null;
    const date = day != null && Number.isFinite(day.ms) ? msToIsoDay(day.ms) : null;
    // `route`: the two stops for the Omio link's position lookup
    // (features/journeys/omioPositions.ts), as on the owner's page.
    const route = leg
      ? { from: { city: from?.city ?? null, country: from?.country ?? null }, to: { city: to?.city ?? null, country: to?.country ?? null } }
      : null;
    return { leg, date, fromCity: from?.city || null, toCity: to?.city || null, route };
  }, [journey]);
  const trackLegClick = useCallback((
    legId: string, source: 'timeline' | 'leg_sheet', date: string | null, mode?: string,
    night?: { partner: 'omio' | 'operator'; operatorKey: string | null; operatorName: string | null },
  ) => {
    if (!journeyId) return;
    const { leg, fromCity, toCity } = legContext(legId);
    const ms = date ? Date.parse(date) : NaN;
    const now = new Date();
    const todayUtc = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
    analytics.trackSharedAffiliateLinkClicked({
      partner: night ? night.partner : 'omio', journey_id: journeyId, leg_id: legId, transport_mode: mode || leg?.transportMode || null,
      operator_key: night ? night.operatorKey : undefined, operator_name: night ? night.operatorName : undefined,
      from_city: fromCity, to_city: toCity, source,
      days_until_departure: Number.isFinite(ms) ? Math.round((ms - todayUtc) / 86400000) : null,
    });
  }, [journeyId, legContext]);
  const sharedOmioLink = useCallback(async (legId: string, source: 'timeline' | 'leg_sheet', date: string | null) => {
    const link = await fetchSharedLegOmioLink(token!, journeyId!, legId, { date, route: legContext(legId).route });
    trackLegClick(legId, source, date);
    return link;
  }, [token, journeyId, legContext, trackLegClick]);
  const sharedNightTrainLink = useCallback(async (legId: string, source: 'timeline' | 'leg_sheet', date: string | null) => {
    // The Omio lookup only for a train sold on Omio.
    const { leg, route } = legContext(legId);
    const link = await fetchSharedNightTrainBookingLink(token!, journeyId!, legId, { date, route: leg?.nightTrain?.preferredChannel === 'omio' ? route : null });
    // Every night-train booking click, the partner telling Omio from the operator's site.
    trackLegClick(legId, source, date, 'night_train', { partner: link.channel === 'omio' ? 'omio' : 'operator', operatorKey: link.operatorKey ?? null, operatorName: link.operatorName ?? null });
    return link;
  }, [token, journeyId, legContext, trackLegClick]);
  const sharedStayLink = useCallback(async (stopId: string, source: 'timeline' | 'stop_sheet', checkin: string | null, checkout: string | null) => {
    const link = await fetchSharedStopBookingLink(token!, journeyId!, stopId, { checkin, checkout });
    const stop = (journey?.stops || []).find((s: any) => s.id === stopId);
    analytics.trackSharedAffiliateLinkClicked({ partner: 'booking', journey_id: journeyId!, stop_id: stopId, city: stop?.city || null, source });
    return link;
  }, [token, journeyId, journey]);

  const handleLegBooking = useCallback((legId: string) => {
    if (busyLegId) return;
    const { leg, date } = legContext(legId);
    const night = isNightTrainMode(leg?.transportMode);
    setBusyLegId(legId);
    openInNewTab(
      night ? sharedNightTrainLink(legId, 'timeline', date) : sharedOmioLink(legId, 'timeline', date),
      night ? 'Could not open the booking page' : 'Could not open the ticket search',
    ).finally(() => setBusyLegId(null));
  }, [busyLegId, legContext, openInNewTab, sharedNightTrainLink, sharedOmioLink]);
  const handleStopBooking = useCallback((stopId: string) => {
    if (busyStopId || !journey) return;
    const w = journey.departureDate
      ? computeJourneySchedule(journey.stops || [], journey.legs || [], journey.departureDate).stopWindows[stopId]
      : null;
    setBusyStopId(stopId);
    openInNewTab(
      sharedStayLink(stopId, 'timeline', w ? msToIsoDay(w.fromMs) : null, w ? msToIsoDay(w.toMs) : null),
      'Could not open the stay search',
    ).finally(() => setBusyStopId(null));
  }, [busyStopId, journey, openInNewTab, sharedStayLink]);
  // The sheets open the tab themselves; they only need the link.
  const sheetLegLinks = useMemo<LegBookingLinks>(() => ({
    omio: (legId, ctx) => sharedOmioLink(legId, 'leg_sheet', ctx.date),
    nightTrain: (legId, ctx) => sharedNightTrainLink(legId, 'leg_sheet', ctx.date),
  }), [sharedOmioLink, sharedNightTrainLink]);
  const sheetStayLink = useCallback(
    (stopId: string, ctx: { checkin: string | null; checkout: string | null }) => sharedStayLink(stopId, 'stop_sheet', ctx.checkin, ctx.checkout),
    [sharedStayLink],
  );

  // Never persists on this page; the wall is the answer to any save attempt.
  const wallSave = useCallback(async () => { setWallOpen(true); return false; }, []);

  /**
   * Capture-phase gate shared by the timeline and the sheets: inner components
   * must never run their own click handlers here (some persist themselves with
   * the viewer's credentials, e.g. NightsStepper's debounced PUT). Clicks on
   * plain text pass; controls that merely OPEN a read-only surface (the stop /
   * leg rows, the booked pills, the sheets' Close) are allowed through by
   * aria-label, and so is any control marked `data-share-ok` by its own
   * component (section toggles, booking buttons: they edit nothing);
   * everything else opens the share wall. If an allowed label changes in
   * RouteTimeline/BottomSheet, this fails SAFE (walls instead of opening).
   */
  const ALLOWED_LABELS = useMemo(() => /^(Travel details:|Stay details:|Booked\.|Not booked\.|Close$)/, []);
  const guardInteraction = useCallback((e: React.MouseEvent) => {
    const el = e.target as HTMLElement;
    const interactive = el.closest('button, input, select, textarea, [role="button"], [role="switch"], a') as HTMLElement | null;
    if (!interactive) return;
    const label = interactive.getAttribute('aria-label') || '';
    if (ALLOWED_LABELS.test(label) || interactive.hasAttribute('data-share-ok')) return;
    e.preventDefault();
    e.stopPropagation();
    openWall();
  }, [ALLOWED_LABELS, openWall]);

  // Save a copy = the WHOLE trip (all alternatives). Same contract as the
  // owner's Create copy (Paulo 2026-09-14): the shared NameDialog first,
  // prefilled "Copy of …"; the visitor lands on this journey's copy.
  const [copyPromptOpen, setCopyPromptOpen] = useState(false);
  const requestSaveCopy = useCallback(() => {
    if (!signedIn) { setWallOpen(true); return; }
    setCopyPromptOpen(true);
  }, [signedIn]);
  const handleSaveCopy = useCallback(async (title: string) => {
    if (!token || !journeyId) return;
    setSavingCopy(true);
    try {
      const created = await saveSharedCopy(token, title);
      const focus = created.journeyIds[journeyId] || created.mainJourneyId;
      showToast({ title: 'Saved', message: `"${title}" and all its routes are now in My Trips.`, type: 'success' });
      navigate(`/trips/${encodeURIComponent(created.tripId)}${focus ? `?focus=${encodeURIComponent(focus)}` : ''}`);
    } catch (e: any) {
      showToast({ title: 'Error', message: e?.response?.data?.error || 'Could not save a copy. Please try again.', type: 'error' });
    } finally {
      setSavingCopy(false);
    }
  }, [token, journeyId, navigate, showToast]);

  const handlePassword = useCallback((password: string) => {
    if (!token) return;
    storeSharePassword(token, password);
    load();
  }, [token, load]);

  if (!token || !journeyId || problem === 'not_found') return <ShareNotFound />;
  if (problem === 'expired') return <ShareExpired />;
  if (problem === 'too_many_attempts') return <ShareTooManyAttempts />;
  if (problem === 'network') return <ShareLoadError onRetry={load} />;
  if (problem === 'password_required' || problem === 'password_incorrect') {
    return (
      <SharePasswordGate
        error={problem === 'password_incorrect'}
        busy={loading}
        onSubmit={handlePassword}
      />
    );
  }

  return (
    <div style={{ background: 'var(--bg)', minHeight: '100%' }}>
      <DetailSplitLayout
        map={!loading && routeMap.placement === 'beside' && routeMap.routeStops.length > 0 ? (
          <JourneyRouteMap stops={routeMap.routeStops} highlight={routeMap.highlight} fill {...routeMap.sideMapProps} />
        ) : null}
      >
        {loading || !payload || !journey ? (
          <div style={{ color: '#6c7872', padding: '24px 0' }}>Loading journey…</div>
        ) : (
          <>
            <TripDetailNav
              title={journey.title || 'Journey'}
              onBack={() => navigate(`/s/${encodeURIComponent(token)}`)}
              titleBadge={journey.isMain ? <MainTag /> : null}
              actions={(
                <SharedNavActions
                  onShare={() => setShareOpen(true)}
                  onSaveCopy={requestSaveCopy}
                  savingCopy={savingCopy}
                />
              )}
            />

            <SharedTripStrip ownerFirstName={payload.share.ownerFirstName} />

            {routeMap.placement !== 'beside' && routeMap.routeStops.length > 0 && (
              <div style={{ borderRadius: 16, overflow: 'hidden', marginBottom: 12 }}>
                <JourneyRouteMap stops={routeMap.routeStops} height={240} />
              </div>
            )}

            <div style={{ marginBottom: 16 }}>
              <InfoPills
                stops={journey.stops || []}
                legs={journey.legs || []}
                costEstimating={false}
                invitePricing={false}
              />
            </div>

            {Array.isArray(journey.tips) && journey.tips.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <JourneyTips tips={journey.tips} />
              </div>
            )}

            {/* Every edit affordance stays visible (handlers passed so the
                controls render); guardInteraction (capture phase) walls edits
                while letting the stop/leg rows open their read-only sheets. */}
            <div
              style={{ background: '#ffffff', borderRadius: 16, padding: 16, marginBottom: 16 }}
              onClickCapture={guardInteraction}
            >
              <RouteTimeline
                stops={journey.stops || []}
                legs={journey.legs || []}
                departureDate={journey.departureDate}
                returnDate={journey.returnDate}
                onAddStop={openWall}
                onRemoveStop={openWall}
                onReplaceStop={openWall}
                onOpenLeg={(legId) => setOpenLegId(legId)}
                onOpenStop={(stopId) => setOpenStopId(stopId)}
                onNightsChange={openWall}
                onToggleStopPlanned={openWall}
                onToggleLegPlanned={openWall}
                onChangeLegMode={openWall}
                onOmioLeg={handleLegBooking}
                onNightTrainBooking={handleLegBooking}
                omioBusyLegId={busyLegId}
                onBookingStop={handleStopBooking}
                bookingBusyStopId={busyStopId}
                {...routeMap.timelineProps}
              />
            </div>

            <ApproxNote />
          </>
        )}
      </DetailSplitLayout>

      {/* Read-only detail sheets (portals; React still routes their events
          through this tree, so the same capture gate applies). */}
      <div onClickCapture={guardInteraction}>
        <LegSheet
          journeyId={null}
          open={!!openLeg}
          leg={openLeg}
          from={openLegStops.from}
          to={openLegStops.to}
          route={openLeg ? legContext(openLeg.id).route : null}
          busy={false}
          expectedDate={openLegExpectedDate}
          changeAtFrom={Boolean(openLeg && changeIds.has(openLeg.fromStopId))}
          changeAtTo={Boolean(openLeg && changeIds.has(openLeg.toStopId))}
          onSave={wallSave}
          onClose={() => setOpenLegId(null)}
          bookingLinks={sheetLegLinks}
        />
        <StopSheet
          journeyId={null}
          open={!!openStop}
          stop={openStop}
          busy={false}
          checkin={openStopWindow.checkin}
          checkout={openStopWindow.checkout}
          dateLabel={openStopWindow.label}
          onNightsChange={openWall}
          bookingLink={sheetStayLink}
          onSave={wallSave}
          onReplace={openWall}
          onDelete={openWall}
          onClose={() => setOpenStopId(null)}
        />
      </div>

      {shareOpen && payload && journey && (
        <SharedShareDialog
          token={token}
          tripId={journey.tripId || ''}
          journeyId={journey.id}
          tripTitle={payload.share.tripTitle}
          source="shared_journey"
          onClose={() => setShareOpen(false)}
        />
      )}

      {copyPromptOpen && payload && (
        <NameDialog
          title="Save a copy"
          label="Name for the trip copy"
          confirmText="Save copy"
          initialValue={`Copy of ${payload.share.tripTitle}`}
          busy={savingCopy}
          onConfirm={handleSaveCopy}
          onCancel={() => { if (!savingCopy) setCopyPromptOpen(false); }}
        />
      )}

      {wallOpen && payload && (
        <ShareWallDialog
          token={token}
          signedIn={signedIn}
          ownerFirstName={payload.share.ownerFirstName}
          savingCopy={savingCopy}
          onSaveCopy={() => { setWallOpen(false); requestSaveCopy(); }}
          onClose={() => setWallOpen(false)}
        />
      )}
    </div>
  );
};

export default SharedJourneyPage;
